import React, { useEffect, useState } from "react";
import classes from "./TopCities.module.scss";
import CustomImage from "../../CustomImage";
import useIsMobile from "../../../hooks/useIsMobile";
import useEmblaOverflow from "../../../hooks/useEmblaOverflow";
import fetchUtil from "../../../utils/fetchUtil";
import { getSlugFromCountryUniDetail } from "../../../utils/helpers";
import { unicountries } from "../../../utils/constants";
import Spinner from "../../UI/Spinner/Spinner";

interface TopCitiesProps {
  countryData?: any;
}

const TopCities: React.FC<TopCitiesProps> = ({ countryData }) => {
  const citiesData = countryData?.studentFriendlyCities ?? {};
  const defaultActiveId = citiesData?.cities?.[0]?.id ?? 1;
  const [activeId, setActiveId] = useState(defaultActiveId);
  const [activeCity, setActiveCity] = useState<any>(null);
  const [activeCityUniversities, setActiveCityUniversities] = useState<any>([]);
  const [uniLoading, setUniLoading] = useState(false);
  const isMobile = useIsMobile(1250);
  const { emblaRef, canScrollPrev, canScrollNext, scrollPrev: goToPrev, scrollNext: goToNext } = useEmblaOverflow();

  useEffect(() => {
    let cancelled = false;
    const activeCity = citiesData?.cities?.find((c: any) => c.id === activeId);
    setActiveCity(activeCity);
    setUniLoading(true);

    const getUniIds = activeCity?.universities?.map((uni: any) => uni?.university?.id) ?? [];

    if (getUniIds.length === 0) {
      setActiveCityUniversities([]);
      setUniLoading(false);
      return;
    }

    const buildUrl = `/universities?id_in=${getUniIds.join("&id_in=")}&_publicationState=live&_locale=en`;
    fetchUtil(`${process.env.NEXT_PUBLIC_API_BASE_URL}${buildUrl}`, "GET", {}, "", false)
      .then((res: any) => {
        if (cancelled) return;
        const universities = Array.isArray(res) ? res : [];
        const orderedUniversities = getUniIds.map((id: any) => universities.find((uni: any) => uni.id === id)).filter(Boolean);
        setActiveCityUniversities(orderedUniversities);
      })
      .catch((err: any) => {
        if (!cancelled) setActiveCityUniversities([]);
      })
      .finally(() => {
        if (!cancelled) setUniLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeId]);

  const handleCityClick = (city: any) => {
    setActiveId(city.id);
  };

  const arrowLeft = (
    <div
      className={`${classes["tc-arrow"]}${canScrollPrev ? "" : ` ${classes["tc-arrow-disabled"]}`}`}
      onClick={canScrollPrev ? goToPrev : undefined}
      aria-disabled={!canScrollPrev}
    >
      <CustomImage src="/new-images/left-arrow.svg" height={17} width={10} alt="Previous" />
    </div>
  );

  const arrowRight = (
    <div
      className={`${classes["tc-arrow"]}${canScrollNext ? "" : ` ${classes["tc-arrow-disabled"]}`}`}
      onClick={canScrollNext ? goToNext : undefined}
      aria-disabled={!canScrollNext}
    >
      <CustomImage src="/new-images/right-arrow.svg" height={17} width={10} alt="Next" />
    </div>
  );

  return (
    <section id="cities" className={classes["tc-section"]}>
      <div className={classes["tc-container"]}>
        <h2 className={classes["tc-heading"]}>{citiesData?.title ?? ""}</h2>
        <div className={classes["tc-subtext"]} dangerouslySetInnerHTML={{ __html: citiesData?.description ?? "" }}></div>

        {isMobile ? (
          <div className={classes["tc-carousel-wrap"]}>
            <div className={classes["tc-viewport"]} ref={emblaRef}>
              <div className={classes["tc-slider"]}>
                {citiesData?.cities?.map((city: any) => (
                  <div key={city.id} className={classes["tc-slide"]}>
                    <button className={`${classes["tc-pill"]} ${activeId === city.id ? classes["tc-pill--active"] : ""}`} onClick={() => handleCityClick(city)}>
                      {city.name}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className={classes["tc-arrows-below"]}>
              {arrowLeft}
              {arrowRight}
            </div>
          </div>
        ) : (
          <div className={classes["tc-carousel-wrap--desktop"]}>
            {arrowLeft}
            <div className={classes["tc-viewport"]} ref={emblaRef}>
              <div className={classes["tc-slider"]}>
                {citiesData?.cities?.map((city: any) => (
                  <div key={city.id} className={classes["tc-slide"]}>
                    <button className={`${classes["tc-pill"]} ${activeId === city.id ? classes["tc-pill--active"] : ""}`} onClick={() => handleCityClick(city)}>
                      {city.name}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            {arrowRight}
          </div>
        )}

        <div className={classes["tc-detail"]}>
          <div className={classes["tc-detail__info"]}>
            <h3 className={classes["tc-detail__heading"]}>{activeCity?.aboutHeading ?? `About studying in ${activeCity?.name}`}</h3>
            <p className={classes["tc-detail__desc"]}>{activeCity?.description}</p>

            {activeCity?.universities && activeCity?.universities?.length > 0 && (
              <>
                {activeCity.image && isMobile && (
                  <div className={classes["tc-detail__image"]}>
                    <CustomImage
                      src={activeCity?.image?.url ?? "/new-images/default-image.jpg"}
                      alt={activeCity?.name}
                      layout="fill"
                      objectFit="fill"
                      height="100%"
                      width="100%"
                    />
                  </div>
                )}
                <h4 className={classes["tc-detail__uni-heading"]}>{activeCity?.universityListHeading ?? `Top Universities to Study in ${activeCity?.name}`}</h4>
                <div className={classes["tc-detail__uni-list"]}>
                  {uniLoading ? (
                    <div className={classes["loading-container"]}>
                      <Spinner />
                    </div>
                  ) : (
                    activeCityUniversities?.length > 0 &&
                    activeCityUniversities?.map((uni: any, idx: number) => {
                      const slug = uni.slug;
                      const url = slug && uni.country ? `/universities/${getSlugFromCountryUniDetail(uni.country, unicountries)}/${slug}` : "";

                      return (
                        <div
                          key={idx}
                          className={`${classes["tc-detail__uni-row"]} ${url ? classes["tc-detail__uni-row--clickable"] : ""}`}
                          onClick={url ? () => window.open(url, "_blank") : undefined}
                        >
                          <div className={classes["tc-detail__uni-logo"]}>
                            <CustomImage
                              src={uni.image?.imageUrl ?? "/new-images/default-logo.svg"}
                              width={60}
                              height={40}
                              alt={uni.name}
                              objectFit="contain"
                            />
                          </div>
                          <span className={classes["tc-detail__uni-name"]}>{uni?.name}</span>
                          <CustomImage src="/new-images/right-arrow.svg" width={8} height={14} alt="View" className={classes["tc-detail__uni-chevron"]} />
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>

          {activeCity?.image && !isMobile && (
            <div className={classes["tc-detail__image"]}>
              <CustomImage
                src={activeCity?.image?.url ?? "/new-images/default-image.jpg"}
                alt={activeCity?.name}
                layout="fill"
                objectFit="fill"
                height="100%"
                width="100%"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TopCities;
